import React, { useState, useRef, useEffect } from 'react';
import { LinearProgress, Fab, Typography, Grid, TextField, Button, Card, CardContent, CardActions, FormControlLabel, Checkbox, Paper, IconButton, CircularProgress } from '@material-ui/core'
import Selector from '../../components/Common/Selector/Selector';
import withStyles from '@material-ui/core/styles/withStyles';
import PhoneNumberTextField from '../../components/Common/PhoneNumberTextField/PhoneNumberTextField';
import Auth from '../../services/auth';
import PubSub from 'pubsub-js';
import { requestedCategories } from './requestCategories';
import { submitSupportRequest } from '../../services/supportService';
import DeleteIcon from '@material-ui/icons/Delete';
import SendIcon from '@material-ui/icons/Send';
import CommonUploader from '../../components/Common/CommonUpload/upload/CommonUploader';
import Alert from '../../components/Alert';
import { useParams } from 'react-router-dom';
import Modal from '@material-ui/core/Modal';
import Box from '@material-ui/core/Box';

const supportFieldValidation = [
    { name: "subject", displayName: 'Subject', required: true },
    { name: "description", displayName: 'Description', required: true },
    { name: "category", displayName: 'Category', required: true },
    { name: "phoneNumber", displayName: 'Phone', required: true, minLength: 10 }
]

const jobFieldValidation = [
    { name: "patientName", displayName: 'Patient Name', required: true },
    { name: "dateOfService", displayName: 'Date of Service', required: true },
]

const styles = theme => ({
    root: theme.mixins.gutters({
        paddingTop: 16,
        paddingBottom: 16,
        marginTop: theme.spacing(2),
    }),
    card: {
        minWidth: 275,
    },
    title: {
        marginBottom: 16,
        fontSize: 14,
    },
    fab: {
        position: 'fixed',
        bottom: theme.spacing(10),
        right: theme.spacing(4),
    },
    extendedIcon: {
        marginRight: theme.spacing(1),
    },
});

const defaultCategory = {
    id: 'other',
    firstMessage: '',
    secondMessage: ''
}

const SupportRequestForm = (props) => {
    const auth = new Auth();
    const { category } = useParams();
    const [loading, setLoading] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [supportRequest, setSupportRequest] = useState({ category: "other", accountNumber: auth.getActiveAccount(), facilityName: auth.getCenterName() })
    const [selectedCategory, setSelectedCategory] = useState(defaultCategory)
    const [jobsToRetrieve, setJobsToRetrieve] = useState([]);
    const [requiredCheckboxes, setRequiredCheckboxes] = useState([]);
    const [files, setFiles] = useState([]);
    const availableAccounts = auth.getUserAccounts();
    const [errors, setErrors] = useState({});
    const formRef = useRef();
    const uploaderRef = useRef();
    const [confirmation, setConfirmation] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    PubSub.subscribe('AccountChanged', () => {
        const activeAccountNumber = auth.getActiveAccount();
        const activeAccount = availableAccounts.find(x => x.Account === activeAccountNumber)
        setSupportRequest({ ...setSupportRequest, accountNumber: activeAccountNumber, facilityName: activeAccount.Name })
    });

    useEffect(() => {
        if (category && category === "archive-request") {
            const archiveCategory = requestedCategories.find(x => x.id === "archiveDocumentRetrieval");
            setSelectedCategory(archiveCategory);
            setRequiredCheckboxes(archiveCategory.requiredCheckboxes);
            setJobsToRetrieve([{ id: 1 }]);
        } 

    }, [])

    useEffect(() => {
        const link = document.getElementById('price-schedule-link');
        if (link) {
            link.onclick = (e) => {
                e.preventDefault();
                setShowModal(true);
            };
        }
        return () => {
            if (link) link.onclick = null;
        };
    }, [selectedCategory]);

    const handleChange = ({ target }) => {
        const { name, value } = target;
        setSupportRequest({ ...supportRequest, [name]: value });
    }

    const handleCategoryChange = ({ target }) => {
        const { name, value } = target;
        const selected = requestedCategories.find(x => x.id === value);
        if (selected && selected.id === "archiveDocumentRetrieval") {
            if (!jobsToRetrieve || jobsToRetrieve.length === 0) {
                setSupportRequest({ ...supportRequest, [name]: value });
                setRequiredCheckboxes(selected.requiredCheckboxes);
                setJobsToRetrieve([{ id: 1 }]);
            }
        } else {
            setRequiredCheckboxes([])
            setJobsToRetrieve([]);
            setSupportRequest({ ...supportRequest, category: "other" });
        }

        setSelectedCategory(selected || defaultCategory);
    }

    const handleRequiredCheckBoxChange = ({ target }, requiredCheckBox) => {
        const { checked } = target;
        const updatedRequiredCheckboxes = [...requiredCheckboxes];
        const targetRequiredCheckbox = updatedRequiredCheckboxes.find(x => x.id === requiredCheckBox.id);
        const targetIndex = updatedRequiredCheckboxes.indexOf(targetRequiredCheckbox);
        targetRequiredCheckbox.checked = checked;
        updatedRequiredCheckboxes.splice(targetIndex, 1, targetRequiredCheckbox);
        setRequiredCheckboxes(updatedRequiredCheckboxes);
    }

    const handleAddJob = () => {
        if (jobsToRetrieve && jobsToRetrieve.length < 4) {
            const updatedJobsToRetrieve = [...jobsToRetrieve];
            updatedJobsToRetrieve.push({ id: updatedJobsToRetrieve.length + 1 });
            setJobsToRetrieve(updatedJobsToRetrieve);
        }
    }

    const handleRemoveJob = (job) => {
        if (jobsToRetrieve && jobsToRetrieve.length > 1) {
            var updatedJobsToRetrieve = [...jobsToRetrieve];
            const targetJob = jobsToRetrieve.find(x => x.id === job.id);
            var targetIndex = updatedJobsToRetrieve.indexOf(targetJob);
            updatedJobsToRetrieve.splice(targetIndex, 1);
            setJobsToRetrieve(updatedJobsToRetrieve);
        }
    }

    const handleJobChange = ({ target }, job) => {
        const { name, value } = target;
        const updatedJobsToRetrieve = [...jobsToRetrieve];
        const targetJob = jobsToRetrieve.find(x => x.id === job.id);
        const targetIndex = updatedJobsToRetrieve.indexOf(targetJob);
        targetJob[name] = value;
        updatedJobsToRetrieve.splice(targetIndex, 1, targetJob);
        setJobsToRetrieve(updatedJobsToRetrieve);
    }

    const handleFilesLoaded = (loadedFiles) => {
        setFiles(loadedFiles);
    }

    const resetFormData = () => {
        setSelectedCategory(defaultCategory);
        setJobsToRetrieve([]);
        setFiles([]);
        setSupportRequest({ accountNumber: auth.getActiveAccount(), facilityName: auth.getCenterName() });
        formRef.current.reset();
        uploaderRef.current.clearFiles();
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let isFormValid = validateForm();
            if (!isFormValid) {
                setLoading(false);
                return;
            }
            if (selectedCategory.id === "archiveDocumentRetrieval") {
                for (let index in jobsToRetrieve) {
                    const { isValid, validationErrors } = validateFields(jobFieldValidation, jobsToRetrieve[index], index);
                    if (!isValid) {
                        setLoading(false);
                        return;
                    }

                }
                supportRequest.additionalProperties = {
                    JobsToRetrieve: jobsToRetrieve
                }
            }
            supportRequest.category = selectedCategory.id;

            if (files.length > 0) {
                let baseFiles = {};
                for (let file of files) {
                    let baseFile = await toBase64(file);
                    baseFile = baseFile.split(",")[1];
                    baseFiles = { ...baseFiles, [file.name]: baseFile };
                }
                supportRequest.attachments = baseFiles;
            }
            var result = await submitSupportRequest(supportRequest);
            if (result) {
                setLoading(false);
                setConfirmation({
                    text: `Thank you for contacting us! Your case has been received, and our team is working diligently to assist you. We appreciate your patience and cooperation during this process. We will keep you updated and strive to provide a friendly and prompt resolution. If you have any additional information or questions, please let us know. We are here to help`,
                    title: 'Support Request Submitted Successfully'
                })
                setShowConfirmation(true);
                resetFormData();
            }
        }
        catch (error) {
            setConfirmation({
                text: `There was an issue submitting your request. Please contact itsupport@surgicalnotes.com for assistance`,
                title: 'Support Request Failed to Submit'
            })
            setShowConfirmation(true);
            setLoading(false);
        }

    }
    const handleCloseConfirmation = () => {
        setShowConfirmation(false);
    }
    const toBase64 = file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });



    const validateForm = () => {
        let isFormValid = true;
        let formErrors = {};
        const { isValid, validationErrors } = validateFields(supportFieldValidation, supportRequest);
        formErrors = { ...formErrors, ...validationErrors };
        if (!isValid) isFormValid = false

        if (selectedCategory.id === "archiveDocumentRetrieval") {
            for (let index in jobsToRetrieve) {
                const { isValid, validationErrors } = validateFields(jobFieldValidation, jobsToRetrieve[index], index);
                formErrors = { ...formErrors, ...validationErrors };
                if (!isValid) isFormValid = false
            }
            supportRequest.additionalProperties = {
                JobsToRetrieve: jobsToRetrieve
            }
        }
        setErrors(formErrors);
        return isFormValid;
    }
    const validateFields = (validationObject, objectToValidate, index) => {
        let isValid = true;
        let validationErrors = {};
        //required fields
        var requiredFields = validationObject.filter(x => x.required);
        for (let field of requiredFields) {
            if (!objectToValidate[field.name] || objectToValidate[field.name].length === 0) {
                isValid = false;
                if (index) {
                    validationErrors = { ...validationErrors, [`${field.name}-${index}`]: `${field.displayName} is required.` };
                } else {
                    validationErrors = { ...validationErrors, [field.name]: `${field.displayName} is required.` };
                }
            }
        }

        //minLength fields
        var minLenthFields = validationObject.filter(x => x.minLength);
        for (let field of minLenthFields) {
            if (!objectToValidate[field.name] || objectToValidate[field.name].length < field.minLength) {
                isValid = false;
                if (index) {
                    validationErrors = { ...validationErrors, [`${field.name}-${index}`]: `${field.displayName} must be ${field.minLength} digits.` };
                } else {
                    validationErrors = { ...validationErrors, [field.name]: `${field.displayName} must be ${field.minLength} digits.`};
                }
            }
        }
        return { isValid, validationErrors };
    }

    return (
        <React.Fragment>
            <Typography variant="h4">Support Request Form</Typography>
            <Grid container direction='column' spacing={3}>
                <Grid item xs={12} md={6}>
                    <div dangerouslySetInnerHTML={{ __html: selectedCategory.firstMessage }} />
                </Grid>
                <Grid item xs={12} md={6} spacing={3}>
                    {loading ? (
                        <LinearProgress />
                    ) : null}
                    <Card className={props.classes.card}>
                        <CardContent>
                            <form ref={formRef}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <PhoneNumberTextField
                                            label="Phone"
                                            name="phoneNumber"
                                            variant="outlined"
                                            required
                                            onChange={handleChange}
                                            disabled={loading}
                                            fullWidth
                                            value={supportRequest.phoneNumber || ""}
                                            error={errors["phoneNumber"]}
                                            helperText={errors["phoneNumber"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Selector
                                            label="Category"
                                            name="category"
                                            defaultValue={selectedCategory.id || ""}
                                            value={selectedCategory.id || ""}
                                            data={requestedCategories}
                                            keyProperty="id"
                                            displayProperty="name"
                                            required={true}
                                            fullWidth
                                            variant="outlined"
                                            onChange={handleCategoryChange}
                                            disabled={loading}
                                            error={errors["category"]}
                                            helperText={errors["category"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <TextField
                                            label="Subject"
                                            name="subject"
                                            variant="outlined"
                                            fullWidth
                                            inputProps={{ maxLength: 255 }}
                                            onChange={handleChange}
                                            disabled={loading}
                                            required
                                            error={errors["subject"]}
                                            helperText={errors["subject"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <TextField
                                            label="Description"
                                            name="description"
                                            variant="outlined"
                                            required
                                            fullWidth
                                            multiline
                                            rows={4}
                                            inputProps={{ maxLength: 4000 }}
                                            onChange={handleChange}
                                            disabled={loading}
                                            error={errors["description"]}
                                            helperText={errors["description"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Facility Number"
                                            name="accountNumber"
                                            variant="outlined"
                                            onChange={handleChange}
                                            value={supportRequest.accountNumber || ""}
                                            fullWidth
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={8}>
                                        <TextField label="Facility Name"
                                            name="facilityName"
                                            variant="outlined"
                                            value={supportRequest.facilityName || ""}
                                            onChange={handleChange}
                                            fullWidth
                                            disabled
                                        />
                                    </Grid>
                                    {selectedCategory.id === "archiveDocumentRetrieval" ?
                                        (
                                            <Button color="primary" variant="contained" onClick={handleAddJob}>Add Job</Button>
                                        ) :
                                        null
                                    }
                                    {
                                        jobsToRetrieve.map((job, index) => (
                                            <React.Fragment key={index}>
                                                <Grid item xs={12} md={12}>
                                                    <TextField label="Patient Name"
                                                        name="patientName"
                                                        variant="outlined"
                                                        onChange={(e) => handleJobChange(e, job)}
                                                        disabled={loading}
                                                        fullWidth
                                                        error={errors[`patientName-${index}`]}
                                                        helperText={errors[`patientName-${index}`]}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField label="Job Id"
                                                        name="jobId"
                                                        variant="outlined"
                                                        onChange={(e) => handleJobChange(e, job)}
                                                        disabled={loading}
                                                        fullWidth
                                                        required
                                                        error={errors[`jobId-${index}`]}
                                                        helperText={errors[`jobId-${index}`]}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField label="Patient Id"
                                                        name="patientId"
                                                        variant="outlined"
                                                        onChange={(e) => handleJobChange(e, job)}
                                                        disabled={loading}
                                                        fullWidth
                                                        required
                                                        error={errors[`patientId-${index}`]}
                                                        helperText={errors[`patientId-${index}`]}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField label="Physician"
                                                        name="physician"
                                                        variant="outlined"
                                                        onChange={(e) => handleJobChange(e, job)}
                                                        disabled={loading}
                                                        fullWidth
                                                        required
                                                        error={errors[`physician-${index}`]}
                                                        helperText={errors[`physician-${index}`]}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField label="DateOfService"
                                                        name="dateOfService"
                                                        variant="outlined"
                                                        onChange={(e) => handleJobChange(e, job)}
                                                        disabled={loading}
                                                        fullWidth
                                                        required
                                                        error={errors[`dateOfService-${index}`]}
                                                        helperText={errors[`dateOfService-${index}`]}
                                                    />
                                                </Grid>
                                                <Grid>
                                                    <Button disabled={loading} variant="contained" color="secondary" onClick={() => handleRemoveJob(job)} startIcon={<DeleteIcon />}>Remove Job</Button>
                                                </Grid>
                                            </React.Fragment>
                                        ))
                                    }
                                    {
                                        selectedCategory.secondMessage ?
                                            (
                                                <Grid item xs={12} md={12}>
                                                    <TextField
                                                        defaultValue={selectedCategory.secondMessage}
                                                        multiline
                                                        rows={6}
                                                        fullWidth
                                                        variant='outlined'
                                                    />
                                                </Grid>
                                            ) : null
                                    }
                                    {
                                        requiredCheckboxes ?
                                            (
                                                requiredCheckboxes.map((item, key) => (
                                                    <Grid item xs={12} md={12} key={MediaKeyMessageEvent}>
                                                        <FormControlLabel
                                                            key={key}
                                                            control={
                                                                <Checkbox
                                                                    defaultChecked={item.checked}
                                                                    onChange={(e) => handleRequiredCheckBoxChange(e, item)}
                                                                    disabled={loading}
                                                                />
                                                            }
                                                            label={item.title}
                                                        />
                                                    </Grid>
                                                ))
                                            ) : null
                                    }

                                    <Fab
                                        variant="extended"
                                        className={props.classes.fab}
                                        onClick={handleSubmit}
                                        color="primary"
                                        disabled={loading || (selectedCategory.requiredCheckboxes && selectedCategory.requiredCheckboxes.some(x => x.checked === false))}
                                    >
                                        <SendIcon className={props.classes.extendedIcon} />
                                        Submit
                                    </Fab>
                                </Grid>
                            </form>
                        </CardContent>

                    </Card>
                </Grid>
                {
                    selectedCategory.id === "other" ?
                        (
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardContent>
                                        <Typography>Please attach any relevant documents to your support request</Typography>
                                        <CommonUploader
                                            ref={uploaderRef}
                                            onFilesLoaded={handleFilesLoaded}
                                            disabled={loading}
                                        />
                                    </CardContent>
                                    <CardActions>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ) : null
                }
            </Grid>
            <Modal
                open={showModal}
                onClose={() => setShowModal(false)}
                aria-labelledby="price-chart-modal"
                aria-describedby="price-chart-modal-description"
            >
                <Box
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'white',
                        boxShadow: 24,
                        padding: 24,
                        outline: 'none',
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        overflow: 'auto'
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            width: '600px', 
                            height: '370px', 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fff'
                        }}
                    >
                        {imageLoading && (
                            <CircularProgress />
                        )}
                        <img
                            src="/images/price-chart.png"
                            alt="Price Chart"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                display: imageLoading ? 'none' : 'block'
                            }}
                            onLoad={() => setImageLoading(false)}
                            onError={() => setImageLoading(false)}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setShowModal(false)}
                        >
                            Close
                        </Button>
                    </div>
                </Box>
            </Modal>
            <Alert showAlert={showConfirmation}
                alertText={confirmation.text}
                alertTitle={confirmation.title}
                alertOkText={'Close'}
                onAlertOk={handleCloseConfirmation}
            />
        </React.Fragment >
    )
}

export default withStyles(styles, { withTheme: true })(SupportRequestForm);


// WEBPACK FOOTER //
// ./src/Pages/SupportRequestForm/SupportRequestForm.js